// buildSearchParams — pure assembly of the restaurant search params object.
//
// Extracted VERBATIM from `components/RestaurantSearch.js` (the module-level
// `buildSearchParams` helper) as part of the behavior-preserving refactor
// (issue #240). It builds the exact params object that the container hands to
// `searchRestaurants(...)`. The base spread of `filters`, the
// `searchLoc === 'hotel' && selectedHotel` branch (real-address vs.
// `city: selectedHotel.name` when the address is missing or the literal
// 'Address from PDF'), and the `else if (location)` lat/lng-vs-airportCode
// branch are all copied exactly — every conditional and assigned key preserved.
//
// Totality mirrors the source EXACTLY:
//   The source guarded only with the truthiness checks it already performed
//   (`searchLoc === 'hotel' && selectedHotel`, `selectedHotel.address &&
//   selectedHotel.address !== 'Address from PDF'`, `location`,
//   `location.latitude && location.longitude`, `location.airportCode`). It did
//   NOT guard `filters`, `location`, or `selectedHotel` beyond those checks, so
//   NO additional guards are added here. `{ ...filters }` reproduces the
//   source's spread (which would throw on a non-spreadable `filters` exactly as
//   the source did — behavior preserved, not "fixed").
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Build the search params object from location, hotel, and filters.
 *
 * @param {string} searchLoc - The selected search location type ('hotel' | 'airport').
 * @param {Object} location - The trip location ({ latitude?, longitude?, airportCode?, address? }).
 * @param {Object} selectedHotel - The selected hotel ({ name?, address? }).
 * @param {Object} filters - The active filter values (spread into the result).
 * @returns {Object} The params object passed to `searchRestaurants`.
 */
const buildSearchParams = (searchLoc, location, selectedHotel, filters) => {
  const params = { ...filters };

  if (searchLoc === 'hotel' && selectedHotel) {
    if (selectedHotel.address && selectedHotel.address !== 'Address from PDF') {
      params.address = selectedHotel.address;
      params.hotelName = selectedHotel.name;
    } else {
      params.city = selectedHotel.name;
    }
  } else if (location) {
    if (location.latitude && location.longitude) {
      params.latitude = location.latitude;
      params.longitude = location.longitude;
    } else if (location.airportCode) {
      params.airportCode = location.airportCode;
    }
  }

  return params;
};

export { buildSearchParams };
export default buildSearchParams;
